import JSZip from 'jszip';
import { guessField } from '@pti/domain';

/**
 * A Word document opened in the browser for preparing as a template. Fields are written into the document's own XML
 * as {{key}} in a run of their own, using the formatting of the text around them, so the rest of the document — fonts,
 * logos, headers, tables, numbering — is untouched.
 *
 * Positions are "paragraph offsets": the paragraph's text with each tab and line break counted as one character.
 */
export const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const MC = 'http://schemas.openxmlformats.org/markup-compatibility/2006';
const XML_NS = 'http://www.w3.org/XML/1998/namespace';
const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const FIELD_IN_TEXT = /\{\{\s*([\w.]+)\s*\}\}/g;

export interface Part { name: string; label: string; doc: XMLDocument }
export interface DocxModel { zip: JSZip; parts: Part[] }

/** Where a field goes: a paragraph (by its index in reading order within the part) and a range in it. */
export interface Spot { part: string; pid: number; start: number; end: number }

const isW = (n: Node | null, local: string): n is Element => !!n && n.nodeType === 1 && (n as Element).namespaceURI === W && (n as Element).localName === local;
const wChildren = (el: Element, local?: string) => Array.from(el.childNodes).filter((n): n is Element => n.nodeType === 1 && (n as Element).namespaceURI === W && (!local || (n as Element).localName === local));

// ───────────────────────────── Opening and saving ─────────────────────────────

export async function openDocx(file: Blob): Promise<DocxModel> {
  let zip: JSZip;
  try { zip = await JSZip.loadAsync(file); } catch { throw new Error('This is not a Word document (.docx). If it is an older .doc file, open it in Word and use Save As → Word Document (.docx).'); }
  const names = Object.keys(zip.files).filter((n) => /^word\/(document|header\d*|footer\d*)\.xml$/.test(n));
  if (!names.includes('word/document.xml')) throw new Error('This file has no document body. Is it a Word document (.docx)?');
  const order = (n: string) => (n.includes('header') ? 0 : n.includes('document') ? 1 : 2);
  names.sort((a, b) => order(a) - order(b) || a.localeCompare(b, undefined, { numeric: true }));
  const parser = new DOMParser();
  let h = 0; let f = 0;
  const parts: Part[] = [];
  for (const name of names) {
    const doc = parser.parseFromString(await zip.file(name)!.async('string'), 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) throw new Error(`Part of the document could not be read (${name}).`);
    const label = name.includes('header') ? `Header${++h > 1 ? ` ${h}` : ''}` : name.includes('footer') ? `Footer${++f > 1 ? ` ${f}` : ''}` : 'Document';
    parts.push({ name, label, doc });
  }
  const model = { zip, parts };
  for (const p of parts) { unwrapMergeFields(p.doc); normaliseFields(model, p.name); }
  return model;
}

export async function saveDocx(model: DocxModel): Promise<Blob> {
  const ser = new XMLSerializer();
  for (const p of model.parts) {
    let xml = ser.serializeToString(p.doc);
    if (!xml.startsWith('<?xml')) xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n' + xml;
    model.zip.file(p.name, xml);
  }
  return model.zip.generateAsync({ type: 'blob', mimeType: DOCX_TYPE, compression: 'DEFLATE' });
}

/** Undo support: the XML of every part. */
export function snapshot(model: DocxModel): string[] {
  const ser = new XMLSerializer();
  return model.parts.map((p) => ser.serializeToString(p.doc));
}
export function restore(model: DocxModel, snap: string[]) {
  const parser = new DOMParser();
  model.parts.forEach((p, i) => { p.doc = parser.parseFromString(snap[i]!, 'application/xml'); });
}

// ───────────────────────────── Structure ─────────────────────────────

/** Block-level content in reading order: paragraphs and tables (including content controls), as Word shows them. */
export type Block = { type: 'p'; el: Element; pid: number } | { type: 'table'; rows: Block[][][] };

export function blocks(part: Part): Block[] {
  const root = part.doc.documentElement;
  const container = isW(root, 'document') ? wChildren(root, 'body')[0] : root;
  let pid = 0;
  const walk = (el: Element): Block[] => {
    const out: Block[] = [];
    for (const c of wChildren(el)) {
      if (c.localName === 'p') out.push({ type: 'p', el: c, pid: pid++ });
      else if (c.localName === 'tbl') out.push({ type: 'table', rows: wChildren(c, 'tr').map((tr) => wChildren(tr, 'tc').map((tc) => walk(tc))) });
      else if (c.localName === 'sdt') { const content = wChildren(c, 'sdtContent')[0]; if (content) out.push(...walk(content)); }
      else if (['customXml', 'smartTag'].includes(c.localName)) out.push(...walk(c));
    }
    return out;
  };
  return container ? walk(container) : [];
}

export function paragraphs(part: Part): Element[] {
  const out: Element[] = [];
  const visit = (bs: Block[]) => { for (const b of bs) { if (b.type === 'p') out.push(b.el); else b.rows.forEach((r) => r.forEach(visit)); } };
  visit(blocks(part));
  return out;
}

/** One unit of paragraph text: a w:t (its text), a tab or a line break (one character each). */
export interface Atom { el: Element; run: Element; start: number; len: number; kind: 'text' | 'tab' | 'br'; text: string }

function ownParagraph(el: Element): Element | null {
  let n: Node | null = el.parentNode;
  while (n && !isW(n, 'p')) {
    if (n.nodeType === 1 && (n as Element).namespaceURI === MC && (n as Element).localName === 'Fallback') return null;
    n = n.parentNode;
  }
  return n as Element | null;
}

export function atoms(p: Element): Atom[] {
  const out: Atom[] = [];
  let pos = 0;
  const all = p.getElementsByTagNameNS(W, '*');
  for (let i = 0; i < all.length; i++) {
    const el = all[i]!;
    const run = el.parentNode as Element;
    if (!isW(run, 'r') || ownParagraph(el) !== p) continue;
    if (el.localName === 't') { const text = el.textContent ?? ''; out.push({ el, run, start: pos, len: text.length, kind: 'text', text }); pos += text.length; }
    else if (el.localName === 'tab') { out.push({ el, run, start: pos, len: 1, kind: 'tab', text: '\t' }); pos += 1; }
    else if (el.localName === 'br' || el.localName === 'cr') { out.push({ el, run, start: pos, len: 1, kind: 'br', text: '\n' }); pos += 1; }
    else if (el.localName === 'noBreakHyphen') { out.push({ el, run, start: pos, len: 1, kind: 'text', text: '-' }); pos += 1; }
  }
  return out;
}

export const paraText = (p: Element) => atoms(p).map((a) => a.text).join('');

// ───────────────────────────── Editing ─────────────────────────────

function newText(doc: XMLDocument, text: string): Element {
  const t = doc.createElementNS(W, 'w:t');
  t.setAttributeNS(XML_NS, 'xml:space', 'preserve');
  t.textContent = text;
  return t;
}

/** A run with the same formatting as `like` (or the paragraph mark's formatting) holding {{key}}. */
function fieldRun(doc: XMLDocument, p: Element, like: Element | null, key: string): Element {
  const r = doc.createElementNS(W, 'w:r');
  const rPr = like ? wChildren(like, 'rPr')[0] : wChildren(wChildren(p, 'pPr')[0] ?? p, 'rPr')[0];
  if (rPr) {
    const copy = rPr.cloneNode(true) as Element;
    // Don't carry over things that would hide the field or tie it to a revision.
    for (const c of wChildren(copy)) if (['vanish', 'del', 'ins', 'rPrChange', 'webHidden'].includes(c.localName)) copy.removeChild(c);
    r.appendChild(copy);
  }
  r.appendChild(newText(doc, `{{${key}}}`));
  return r;
}

/** Split `run` so that everything before (atom, k) stays in the first half. Returns [before, after] (either may be null). */
function splitRun(atom: Atom, k: number): [Element | null, Element | null] {
  const { run, el } = atom;
  const doc = run.ownerDocument;
  const kids = Array.from(run.childNodes);
  const idx = kids.indexOf(el);
  const rPr = wChildren(run, 'rPr')[0];
  const shell = () => { const r = run.cloneNode(false) as Element; if (rPr) r.appendChild(rPr.cloneNode(true)); return r; };
  const a = shell(); const b = shell();
  let aHas = false; let bHas = false;
  kids.forEach((c, i) => {
    if (c === rPr) return;
    if (i < idx) { a.appendChild(c.cloneNode(true)); aHas = true; }
    else if (i > idx) { b.appendChild(c.cloneNode(true)); bHas = true; }
    else if (atom.kind === 'text' && el.localName === 't') {
      const t = el.textContent ?? '';
      if (k > 0) { a.appendChild(newText(doc, t.slice(0, k))); aHas = true; }
      if (k < t.length) { b.appendChild(newText(doc, t.slice(k))); bHas = true; }
    } else if (k === 0) { b.appendChild(c.cloneNode(true)); bHas = true; } else { a.appendChild(c.cloneNode(true)); aHas = true; }
  });
  return [aHas ? a : null, bHas ? b : null];
}

/** Put {{key}} at a position in a paragraph. */
export function insertField(p: Element, pos: number, key: string, formatLike?: Element | null) {
  const doc = p.ownerDocument;
  const as = atoms(p);
  if (!as.length) {
    // Empty paragraph: add the field after any properties.
    p.appendChild(fieldRun(doc, p, formatLike ?? null, key));
    return;
  }
  // At a boundary, continue the formatting of the text before the cursor (as Word does).
  const atom = as.find((a) => pos > a.start && pos < a.start + a.len)
    ?? [...as].reverse().find((a) => a.start + a.len === pos)
    ?? as.find((a) => a.start === pos)
    ?? as[as.length - 1]!;
  const k = Math.max(0, Math.min(atom.len, pos - atom.start));
  const field = fieldRun(doc, p, formatLike ?? atom.run, key);
  const [before, after] = splitRun(atom, k);
  const parent = atom.run.parentNode!;
  if (before) parent.insertBefore(before, atom.run);
  parent.insertBefore(field, atom.run);
  if (after) parent.insertBefore(after, atom.run);
  parent.removeChild(atom.run);
}

/** Remove the text between two positions in a paragraph. Returns the run the removed text started in. */
export function deleteRange(p: Element, start: number, end: number): Element | null {
  let firstRun: Element | null = null;
  for (const a of atoms(p)) {
    const s = Math.max(start, a.start); const e = Math.min(end, a.start + a.len);
    if (e <= s) continue;
    firstRun ??= a.run;
    if (a.kind === 'text' && a.el.localName === 't') {
      const t = a.el.textContent ?? '';
      a.el.textContent = t.slice(0, s - a.start) + t.slice(e - a.start);
      if (!a.el.textContent) a.el.parentNode?.removeChild(a.el); else a.el.setAttributeNS(XML_NS, 'xml:space', 'preserve');
    } else a.el.parentNode?.removeChild(a.el);
  }
  // Remove runs left with nothing in them (keeping the formatting reference for the caller).
  const runs = p.getElementsByTagNameNS(W, 'r');
  for (const r of Array.from(runs)) {
    if (r === firstRun) continue;
    if (wChildren(r).every((c) => c.localName === 'rPr') && r.parentNode) r.parentNode.removeChild(r);
  }
  return firstRun;
}

/** Replace text with a field, keeping the replaced text's formatting. */
export function replaceWithField(p: Element, start: number, end: number, key: string) {
  const like = deleteRange(p, start, end);
  const keep = like ? (like.cloneNode(true) as Element) : null;
  if (like && like.parentNode && wChildren(like).every((c) => c.localName === 'rPr')) like.parentNode.removeChild(like);
  insertField(p, start, key, keep);
}

export function paragraphAt(model: DocxModel, part: string, pid: number): Element | null {
  const pt = model.parts.find((x) => x.name === part);
  return pt ? paragraphs(pt)[pid] ?? null : null;
}

/** Word sometimes splits text typed as {{field}} across several runs; join each into one run so it shows as one field. */
export function normaliseFields(model: DocxModel, partName: string) {
  const part = model.parts.find((p) => p.name === partName)!;
  for (const p of paragraphs(part)) {
    for (let guard = 0; guard < 50; guard++) {
      const as = atoms(p);
      const text = as.map((a) => a.text).join('');
      const split = [...text.matchAll(FIELD_IN_TEXT)].find((m) => {
        const s = m.index!; const e = s + m[0].length;
        return !as.some((a) => a.kind === 'text' && a.start <= s && a.start + a.len >= e);
      });
      if (!split) break;
      replaceWithField(p, split.index!, split.index! + split[0].length, split[1]!);
    }
  }
}

/** Word mail-merge fields («Name») become plain text, so they can be matched to PTI People fields. */
function unwrapMergeFields(doc: XMLDocument) {
  for (const f of Array.from(doc.getElementsByTagNameNS(W, 'fldSimple'))) {
    if (!/MERGEFIELD/i.test(f.getAttributeNS(W, 'instr') ?? f.getAttribute('w:instr') ?? '')) continue;
    while (f.firstChild) f.parentNode!.insertBefore(f.firstChild, f);
    f.parentNode!.removeChild(f);
  }
  const stack: { runs: Element[]; instr: string; inResult: boolean }[] = [];
  const doomed: Element[] = [];
  for (const r of Array.from(doc.getElementsByTagNameNS(W, 'r'))) {
    const ch = wChildren(r, 'fldChar')[0];
    const type = ch ? ch.getAttributeNS(W, 'fldCharType') ?? ch.getAttribute('w:fldCharType') : null;
    const top = stack[stack.length - 1];
    if (type === 'begin') { stack.push({ runs: [r], instr: '', inResult: false }); continue; }
    if (!top) continue;
    if (type === 'separate') { top.runs.push(r); top.inResult = true; continue; }
    if (type === 'end') {
      top.runs.push(r); stack.pop();
      if (/MERGEFIELD/i.test(top.instr)) doomed.push(...top.runs);
      continue;
    }
    const instr = wChildren(r, 'instrText');
    if (!top.inResult && instr.length) { top.instr += instr.map((i) => i.textContent).join(''); top.runs.push(r); }
  }
  for (const r of doomed) r.parentNode?.removeChild(r);
}

// ───────────────────────────── Finding things ─────────────────────────────

export interface FieldUse { key: string; count: number }

export function fieldsUsed(model: DocxModel): FieldUse[] {
  const n = new Map<string, number>();
  for (const part of model.parts) for (const p of paragraphs(part)) for (const m of paraText(p).matchAll(FIELD_IN_TEXT)) n.set(m[1]!, (n.get(m[1]!) ?? 0) + 1);
  return [...n.entries()].map(([key, count]) => ({ key, count }));
}

/** Placeholders already in the document — [Employee Name], «Start_Date», <<Pay>>, or blank lines after a label. */
export interface Suggestion {
  id: string; text: string; label: string; count: number; guess: string | null; blank: boolean;
  /** Signature lines are matched one at a time, because each belongs to a different person. */
  where?: { part: string; pid: number; start: number; end: number };
  /** e.g. "Employee" or "For Perth Training Institute": the heading this line sits under. */
  context?: string;
}

const PLACEHOLDER = /\[[^[\]\n{}]{2,50}\]|«[^»\n]{1,50}»|<<[^<>\n]{1,50}>>|_{5,}/g;
const blankLabel = (text: string, at: number) => (text.slice(0, at).replace(/[:\-–\s]+$/, '').split(/[\t\n]/).pop() ?? '').trim().split(/\s+/).slice(-4).join(' ');

/** Text above a line: earlier lines in its table cell, the same column in the rows above, or the previous paragraphs. */
function textAbove(p: Element, all: Element[]): string {
  const parts: string[] = [];
  let tc: Node | null = p.parentNode;
  while (tc && !isW(tc, 'tc')) tc = tc.parentNode;
  if (tc) {
    const cell = tc as Element;
    const row = cell.parentNode as Element;
    const col = wChildren(row, 'tc').indexOf(cell);
    const rows: Element[] = [];
    for (let r = row.previousSibling; r; r = r.previousSibling) if (isW(r, 'tr')) rows.unshift(r as Element);
    for (const r of rows.slice(-4)) { const c = wChildren(r, 'tc')[col]; if (c) parts.push(Array.from(c.getElementsByTagNameNS(W, 'p')).map(paraText).join(' ')); }
    for (const q of wChildren(cell, 'p')) { if (q === p) break; parts.push(paraText(q)); }
  } else {
    const i = all.indexOf(p);
    parts.push(...all.slice(Math.max(0, i - 4), i).map(paraText));
  }
  return parts.join(' \n ');
}

/** Who a signature line belongs to, from the nearest heading above it. */
function signerFrom(context: string): { signer: 'person' | 'director' | 'rtom'; heading: string } | null {
  const t = context.toLowerCase();
  const groups: [RegExp, 'person' | 'director' | 'rtom'][] = [
    [/director|employer|for (and on behalf of )?(perth training|pti)|on behalf of (perth training|pti|the company)|company/g, 'director'],
    [/rto manager/g, 'rtom'],
    [/employee|contractor|candidate|trainer|worker|staff member/g, 'person'],
  ];
  let best: { at: number; signer: 'person' | 'director' | 'rtom'; heading: string } | null = null;
  for (const [re, signer] of groups) for (const m of t.matchAll(re)) if (!best || m.index! > best.at) best = { at: m.index!, signer, heading: context.slice(m.index!, m.index! + m[0].length) };
  return best ? { signer: best.signer, heading: best.heading } : null;
}

const isSignLabel = (label: string) => /sign|witness/i.test(label);
const isDateLabel = (label: string) => /^date\b|date signed|dated/i.test(label.trim());

export function suggestions(model: DocxModel): Suggestion[] {
  const found = new Map<string, Suggestion>();
  for (const part of model.parts) {
    const all = paragraphs(part);
    all.forEach((p, pid) => {
      const text = paraText(p);
      for (const m of text.matchAll(PLACEHOLDER)) {
        const blank = m[0].startsWith('_');
        // A blank line is named after the words before it ("Employee name: ________").
        const label = blank ? blankLabel(text, m.index!) : m[0].replace(/^[[«<]+|[\]»>]+$/g, '').replace(/_/g, ' ').trim();
        if (blank && !label) continue;
        let guess = guessField(label);
        const aboveAndHere = `${textAbove(p, all)} \n ${text.slice(0, m.index)}`;
        const sig = isSignLabel(label) || guess?.startsWith('sign');
        // "Date: ____" straight under a signature line is the date that person signs.
        const signDate = isDateLabel(label) && /signature|signed|sign here/i.test(aboveAndHere);
        if (sig || signDate) {
          const who = signerFrom(`${aboveAndHere} ${label}`);
          const signer = who?.signer ?? 'person';
          guess = sig && !isDateLabel(label) ? `sign.${signer}` : `signdate.${signer}`;
          const id = `${part.name}:${pid}:${m.index}`;
          found.set(id, { id, text: m[0], label, count: 1, guess, blank, where: { part: part.name, pid, start: m.index!, end: m.index! + m[0].length }, context: who?.heading });
          continue;
        }
        const id = blank ? `blank:${label}` : m[0];
        const s = found.get(id);
        if (s) s.count++;
        else found.set(id, { id, text: m[0], label, count: 1, guess, blank });
      }
    });
  }
  return [...found.values()];
}

/** Replace a placeholder with a field: one specific line, or every occurrence (for blanks, only blanks after that label). */
export function replaceAll(model: DocxModel, s: Suggestion, key: string): number {
  if (s.where) {
    const p = paragraphAt(model, s.where.part, s.where.pid);
    if (!p || paraText(p).slice(s.where.start, s.where.end) !== s.text) return 0;
    replaceWithField(p, s.where.start, s.where.end, key);
    return 1;
  }
  let n = 0;
  for (const part of model.parts) for (const p of paragraphs(part)) {
    for (let guard = 0; guard < 100; guard++) {
      const text = paraText(p);
      let hit: { start: number; end: number } | null = null;
      if (s.blank) {
        for (const m of text.matchAll(/_{5,}/g)) {
          if (blankLabel(text, m.index!) === s.label) { hit = { start: m.index!, end: m.index! + m[0].length }; break; }
        }
      } else {
        const i = text.indexOf(s.text);
        if (i >= 0) hit = { start: i, end: i + s.text.length };
      }
      if (!hit) break;
      replaceWithField(p, hit.start, hit.end, key);
      n++;
    }
  }
  return n;
}

// ───────────────────────────── Display ─────────────────────────────

export interface RunFormat { bold?: boolean; italic?: boolean; underline?: boolean; sizePt?: number; caps?: boolean; color?: string }

function onOff(rPr: Element | undefined, local: string): boolean | undefined {
  const el = rPr ? wChildren(rPr, local)[0] : undefined;
  if (!el) return undefined;
  const v = el.getAttributeNS(W, 'val') ?? el.getAttribute('w:val');
  return !(v === '0' || v === 'false' || v === 'none');
}

export function runFormat(run: Element): RunFormat {
  const rPr = wChildren(run, 'rPr')[0];
  const sz = rPr ? wChildren(rPr, 'sz')[0] : undefined;
  const color = rPr ? wChildren(rPr, 'color')[0]?.getAttributeNS(W, 'val') : undefined;
  const half = sz ? Number(sz.getAttributeNS(W, 'val') ?? sz.getAttribute('w:val')) : NaN;
  return {
    bold: onOff(rPr, 'b'), italic: onOff(rPr, 'i'), underline: onOff(rPr, 'u'), caps: onOff(rPr, 'caps'),
    sizePt: Number.isFinite(half) ? half / 2 : undefined, color: color && /^[0-9A-Fa-f]{6}$/.test(color) ? `#${color}` : undefined,
  };
}

export interface ParaFormat { heading: 0 | 1 | 2 | 3; align?: 'left' | 'center' | 'right' | 'justify'; list: boolean; indentPt?: number }

export function paraFormat(p: Element): ParaFormat {
  const pPr = wChildren(p, 'pPr')[0];
  const style = pPr ? (wChildren(pPr, 'pStyle')[0]?.getAttributeNS(W, 'val') ?? '') : '';
  const jc = pPr ? (wChildren(pPr, 'jc')[0]?.getAttributeNS(W, 'val') ?? '') : '';
  const ind = pPr ? wChildren(pPr, 'ind')[0] : undefined;
  const left = ind ? Number(ind.getAttributeNS(W, 'left') ?? ind.getAttributeNS(W, 'start') ?? NaN) : NaN;
  const heading = /^Title$/i.test(style) || /Heading1$|^1$/i.test(style) ? 1 : /Heading2$|Subtitle/i.test(style) ? 2 : /Heading[3-9]$/i.test(style) ? 3 : 0;
  return {
    heading, list: !!(pPr && wChildren(pPr, 'numPr').length) || /List/i.test(style),
    align: jc === 'center' ? 'center' : jc === 'right' || jc === 'end' ? 'right' : jc === 'both' ? 'justify' : undefined,
    indentPt: Number.isFinite(left) && left > 0 ? Math.min(left / 20, 120) : undefined,
  };
}

export function hasDrawing(p: Element): boolean {
  return p.getElementsByTagNameNS(W, 'drawing').length > 0 || p.getElementsByTagNameNS(W, 'pict').length > 0;
}
